import { useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, GripVertical, Plus, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../ui';
import { ProviderType } from '../../gql/types';
import { PROVIDER_TYPES, providerLabel } from '../shared/providerLabels';

// Reorderable provider-preference list for a routing policy (US-8.3.1 · providerOrder · W5 DnD).
//
// TWO equivalent reorder paths, both fully supported (W5 keeps the a11y bar):
//
//   1. Keyboard / pointer buttons — each row carries up/down <button>s that are keyboard operable
//      (real buttons → Space/Enter) with a visible focus ring. Every row announces its 1-based
//      position ("Position N of M"). After a move, focus follows the moved provider to its new
//      position so a keyboard user can press repeatedly without re-finding the control.
//
//   2. Pointer drag-and-drop — each row is HTML5 `draggable` (dependency-free) with a clear grab
//      affordance (grip handle + grab cursor), a live drop indicator on the hovered row, and a
//      dimmed/elevated look on the row being dragged. Dropping reorders to the target slot.
//
// A single aria-live region announces the new position unconditionally after any move (keyboard,
// button, or drag).

export interface ProviderOrderEditorProps {
  value: ProviderType[];
  onChange: (next: ProviderType[]) => void;
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  if (item === undefined) return list;
  next.splice(to, 0, item);
  return next;
}

interface ButtonPair {
  up: HTMLButtonElement | null;
  down: HTMLButtonElement | null;
}

interface LastMove {
  type: ProviderType;
  direction: 'up' | 'down';
}

export function ProviderOrderEditor({ value, onChange }: ProviderOrderEditorProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const available = PROVIDER_TYPES.filter((t) => !value.includes(t));

  // Refs to each row's up/down controls, keyed by provider, so focus can follow a moved item.
  const buttonRefs = useRef<Map<ProviderType, ButtonPair>>(new Map());
  const [lastMove, setLastMove] = useState<LastMove | null>(null);
  const [announcement, setAnnouncement] = useState('');

  // Pointer drag-and-drop state: the index being dragged and the index currently hovered (drop
  // indicator). Both null when no drag is in progress.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const moveItem = (type: ProviderType, from: number, direction: 'up' | 'down') => {
    const to = direction === 'up' ? from - 1 : from + 1;
    const next = move(value, from, to);
    if (next === value) return;
    setLastMove({ type, direction });
    setAnnouncement(`${providerLabel(type)} moved to position ${to + 1} of ${next.length}`);
    onChange(next);
  };

  // Reorder by drag: pull the dragged item out and splice it in at the drop slot. Announces the
  // landing position through the same live region as the button moves.
  const reorderByDrag = (from: number, to: number) => {
    if (from === to || to < 0 || to >= value.length) return;
    const next = move(value, from, to);
    if (next === value) return;
    const moved = value[from];
    if (moved !== undefined) {
      setAnnouncement(`${providerLabel(moved)} moved to position ${to + 1} of ${next.length}`);
    }
    onChange(next);
  };

  // Keep keyboard focus on the just-moved provider's control at its new position. Prefer the
  // same-direction button; fall back to the opposite direction's button when the moved item
  // landed at an end and the same-direction button is now disabled.
  useLayoutEffect(() => {
    if (!lastMove) return;
    const pair = buttonRefs.current.get(lastMove.type);
    if (!pair) return;
    const primary = lastMove.direction === 'up' ? pair.up : pair.down;
    const fallback = lastMove.direction === 'up' ? pair.down : pair.up;
    const target = primary && !primary.disabled ? primary : fallback;
    target?.focus();
    setLastMove(null);
  }, [value, lastMove]);

  const registerButton = (type: ProviderType, key: 'up' | 'down') => {
    return (el: HTMLButtonElement | null) => {
      const existing = buttonRefs.current.get(type) ?? { up: null, down: null };
      buttonRefs.current.set(type, { ...existing, [key]: el });
    };
  };

  return (
    <div className="space-y-3">
      {/* Single polite live region: announces the moved provider's new position after a move. */}
      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>
      <ol
        aria-label="Provider order"
        className="space-y-2"
      >
        {value.length === 0 && (
          <li
            className={`rounded-xl border border-dashed px-4 py-6 text-center text-sm ${
              isDark
                ? 'border-white/[0.1] text-white/40'
                : 'border-slate-300 text-slate-400'
            }`}
          >
            Add at least one provider to define the routing order.
          </li>
        )}

        {value.map((type, index) => {
          const label = providerLabel(type);
          const position = `Position ${index + 1} of ${value.length}`;
          const isDragging = dragIndex === index;
          const isDropTarget =
            overIndex === index && dragIndex !== null && dragIndex !== index;
          return (
            <li
              key={type}
              draggable
              data-testid={`order-row-${type}`}
              data-dragging={isDragging || undefined}
              data-drop-target={isDropTarget || undefined}
              onDragStart={(e) => {
                setDragIndex(index);
                e.dataTransfer.effectAllowed = 'move';
                // Some browsers require data to be set for a drag to start.
                e.dataTransfer.setData('text/plain', type);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (overIndex !== index) setOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex !== null) reorderByDrag(dragIndex, index);
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors ${
                isDragging ? 'opacity-50 shadow-lg' : ''
              } ${
                isDropTarget
                  ? isDark
                    ? 'border-indigo-400/60 bg-indigo-500/10 ring-2 ring-indigo-400/30'
                    : 'border-indigo-400 bg-indigo-50 ring-2 ring-indigo-300'
                  : isDark
                    ? 'border-white/[0.06] bg-white/[0.03]'
                    : 'border-slate-200 bg-slate-50'
              }`}
            >
              <GripVertical
                className={`h-4 w-4 flex-shrink-0 cursor-grab active:cursor-grabbing ${
                  isDark ? 'text-white/30' : 'text-slate-400'
                }`}
                aria-hidden="true"
              />
              <span
                className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-xs font-semibold tabular-nums ${
                  isDark
                    ? 'bg-indigo-500/15 text-indigo-300'
                    : 'bg-indigo-100 text-indigo-600'
                }`}
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <span
                className={`min-w-0 flex-1 truncate text-sm font-medium ${
                  isDark ? 'text-white/85' : 'text-slate-800'
                }`}
              >
                {label}
                {/* Visually-hidden position announcement for assistive tech. */}
                <span className="sr-only"> — {position}</span>
              </span>

              <div className="flex flex-shrink-0 items-center gap-1">
                <button
                  type="button"
                  ref={registerButton(type, 'up')}
                  aria-label={`Move ${label} up`}
                  disabled={index === 0}
                  onClick={() => moveItem(type, index, 'up')}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 disabled:cursor-not-allowed disabled:opacity-30 ${
                    isDark
                      ? 'text-white/50 hover:bg-white/[0.06] hover:text-white'
                      : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700'
                  }`}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  ref={registerButton(type, 'down')}
                  aria-label={`Move ${label} down`}
                  disabled={index === value.length - 1}
                  onClick={() => moveItem(type, index, 'down')}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 disabled:cursor-not-allowed disabled:opacity-30 ${
                    isDark
                      ? 'text-white/50 hover:bg-white/[0.06] hover:text-white'
                      : 'text-slate-400 hover:bg-slate-200 hover:text-slate-700'
                  }`}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${label} from order`}
                  onClick={() => onChange(value.filter((t) => t !== type))}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40 ${
                    isDark
                      ? 'text-white/40 hover:bg-rose-500/10 hover:text-rose-300'
                      : 'text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                  }`}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      {available.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {available.map((type) => (
            <Button
              key={type}
              variant="secondary"
              size="sm"
              onClick={() => onChange([...value, type])}
              leftIcon={<Plus className="h-3.5 w-3.5" aria-hidden="true" />}
              aria-label={`Add ${providerLabel(type)}`}
            >
              {providerLabel(type)}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
